import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { useDatasheetPreviewQuery } from '@features/scripts/hooks/useTestRuns';
import { blockedPointHint } from '@features/scripts/utils/datasheetPreviewHints';
import { GroupPicker } from '@features/scripts/components/tabs/GroupPicker';

import type { ScriptFunctionGroup } from '@features/scripts/components/tabs/GroupPicker';

type Props = {
  scriptId: string;
  selectedGroups?: string[];
  onGroupsChange: (groups: string[] | undefined) => void;
  selectedPoints?: string[];
  onPointsChange?: (points: string[] | undefined) => void;
  disabled?: boolean;
};

const CellGroupPicker: React.FC<Props> = ({
  scriptId,
  selectedGroups,
  onGroupsChange,
  selectedPoints,
  onPointsChange,
  disabled = false
}) => {
  const preview = useDatasheetPreviewQuery(scriptId);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const groups = useMemo<ScriptFunctionGroup[]>(() => {
    if (!preview.data) return [];
    return preview.data.groups.map(group => ({
      id: group.id,
      label: group.label,
      pointCount: group.pointCount,
      blockedPoints: group.resolvable
        ? undefined
        : group.blockedPoints.map(point => ({
            label: point.label,
            hint: blockedPointHint(point, preview.data?.catalogName ?? null)
          }))
    }));
  }, [preview.data]);

  const runnableIds = useMemo(
    () => groups.filter(group => !group.blockedPoints?.length).map(g => g.id),
    [groups]
  );

  const effectiveSelected = selectedGroups ?? runnableIds;

  const handleGroupChange = useCallback(
    (ids: string[]) => {
      // The API rejects an empty selection fail-closed, so refuse the deselection that would
      // empty it rather than letting the user arrive at a state where Start always 400s.
      // Same guard the individual-point picker applies below.
      if (ids.length === 0) return;
      const isAll =
        ids.length === runnableIds.length &&
        runnableIds.every(id => ids.includes(id));
      onGroupsChange(isAll ? undefined : ids);
    },
    [onGroupsChange, runnableIds]
  );

  const pointGroups = useMemo(() => {
    if (!preview.data) return [];
    const selectedGroupSet = new Set(effectiveSelected);
    return preview.data.groups
      .filter(group => group.resolvable && selectedGroupSet.has(group.id))
      .map(group => ({
        id: group.id,
        label: group.label,
        points: (group.resolvedPoints ?? []).map(point => ({
          id: `${point.functionId}:${point.label}`,
          label: point.label
        }))
      }))
      .filter(group => group.points.length > 0);
  }, [preview.data, effectiveSelected]);

  const allPointIds = useMemo(
    () => pointGroups.flatMap(group => group.points.map(p => p.id)),
    [pointGroups]
  );

  const effectivePointSet = useMemo(
    () => new Set(selectedPoints ?? allPointIds),
    [selectedPoints, allPointIds]
  );

  const handleTogglePoint = useCallback(
    (pointId: string) => {
      if (!onPointsChange) return;
      const current = new Set(selectedPoints ?? allPointIds);
      if (current.has(pointId)) current.delete(pointId);
      else current.add(pointId);
      const filtered = allPointIds.filter(id => current.has(id));
      if (filtered.length === 0) return;
      const isAll = filtered.length === allPointIds.length;
      onPointsChange(isAll ? undefined : filtered);
    },
    [onPointsChange, selectedPoints, allPointIds]
  );

  if (groups.length === 0) return null;

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5 }}
      >
        Function groups to run
      </Typography>
      <GroupPicker
        groups={groups}
        selectedIds={effectiveSelected}
        onChange={handleGroupChange}
        disabled={disabled}
      />

      {onPointsChange && allPointIds.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setAdvancedOpen(open => !open)}
            startIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none' }}
            disabled={disabled}
          >
            Advanced: select individual points
          </Button>
          <Collapse in={advancedOpen} mountOnEnter unmountOnExit>
            <Box
              sx={{
                pl: 1,
                pt: 0.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              {pointGroups.map(group => (
                <Box key={group.id}>
                  <Typography variant="overline" color="text.secondary">
                    {group.label}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {group.points.map(point => (
                      <FormControlLabel
                        key={point.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={effectivePointSet.has(point.id)}
                            onChange={() => handleTogglePoint(point.id)}
                            disabled={disabled}
                            sx={{ py: 0.25 }}
                          />
                        }
                        label={
                          <Typography variant="caption">{point.label}</Typography>
                        }
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default CellGroupPicker;
